import DOMPurify from 'dompurify';

/**
 * The line-height a plate block without one of its own lays out at. The frame canvas inherited
 * Bootstrap's unitless body 1.5 on the product page, which is what customers have approved and what
 * the print PDF follows (EngravedPlatePDF.cs FrameCanvasLineHeightFactor). Every surface that shows a
 * plate sets it on its plate box instead of inheriting from the page: the editor's paragraphs used 1,
 * the CAF approval preview and fit check 1, and the image generator's page has no Bootstrap (normal).
 */
export const EP_INHERITED_LINE_HEIGHT = 1.5;

/**
 * The family plate text without a font-family of its own shows in: product.scss gives every element
 * of the frame canvas (`div#frame-container div#ep_bevel *`) and the editor (`.ql-editor`) this chain,
 * so it is also the strut of every Custom paragraph. Surfaces outside the frame set it themselves.
 */
export const EP_FRAME_FONT_FAMILY = "'Century Old Style', Century, 'Times New Roman', Times, serif";

// Default line spacing per plate size (keyed `${width}x${height}` in inches) for
// non-template Custom/SingleDoc plates. Applied to all lines once per size when
// the user switches to that size; re-selecting a size never re-applies so any
// manual line-spacing tweaks the user made afterwards are preserved.
export const EP_SIZE_DEFAULT_LINE_SPACING: Record<string, string> = {
    '3x0.5': '1',
    '3x1': '1',
    '3x2': '1.15',
    '3.5x2.5': '1.25',
    '4.75x2.75': '1.5',
    '6.25x1.75': '1.25',
    '8.125x1.5': '1.25',
};

/**
 * The line spacing the Custom editor gives a line that has none (rich-text paste and undo create
 * them): the plate size's default, else the editor's typing default '1'. A stored paragraph without
 * one would lay out at EP_INHERITED_LINE_HEIGHT instead.
 */
export const epLineSpacingFor = (plateWidthIn: number, plateHeightIn: number): string => EP_SIZE_DEFAULT_LINE_SPACING[`${plateWidthIn}x${plateHeightIn}`] ?? '1';

/**
 * The line spacing of each line of the Custom editor, from the spacing each line carries (undefined for
 * a line without one). A line without its own takes the nearest line's that has one, the line above
 * first, else the first one below, and the plate size's default (epLineSpacingFor) only when no line
 * has any. Rich-text paste and undo create lines without a spacing while the typed lines around them
 * keep theirs, so a pasted paragraph lands at the spacing of the text it joins instead of at the size
 * default next to lines at 1; a paste into the empty editor follows the editor's own line, which Quill
 * keeps at the end of the pasted text, so every line comes out the same.
 */
export const resolveEpLineSpacings = (own: ReadonlyArray<string | undefined>, plateWidthIn: number, plateHeightIn: number): string[] => {
    const spacings: string[] = [];
    let above: string | undefined;
    own.forEach((spacing, i) => {
        const resolved = spacing || above || own.slice(i + 1).find(Boolean) || epLineSpacingFor(plateWidthIn, plateHeightIn);
        spacings.push(resolved);
        above = resolved;
    });
    return spacings;
};

const isTagSpace = (c: string): boolean => c === ' ' || c === '\n' || c === '\t' || c === '\r' || c === '\f';
const isTagNameStart = (c: string): boolean => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');

/**
 * Rewrites the value of every `style` attribute in plate HTML with `mapCss` and leaves everything else
 * byte for byte: text, other attributes (an image's `data:` URI), comments. Attribute values are taken
 * the way the HTML tokenizer takes them (quoted values may hold `>`), so a pattern meant for CSS never
 * reaches a base64 payload or a shopper's text.
 */
export const mapEpStyleAttributes = (html: string, mapCss: (css: string) => string): string => {
    if (!html) return html;
    const n = html.length;
    let out = '';
    let i = 0;
    while (i < n) {
        const lt = html.indexOf('<', i);
        if (lt < 0) return out + html.slice(i);
        out += html.slice(i, lt);
        if (html.startsWith('<!--', lt)) {
            const end = html.indexOf('-->', lt + 4);
            const stop = end < 0 ? n : end + 3;
            out += html.slice(lt, stop);
            i = stop;
            continue;
        }
        if (!isTagNameStart(html[lt + 1] || '')) {
            out += '<';
            i = lt + 1;
            continue;
        }
        let j = lt + 1;
        while (j < n && !isTagSpace(html[j]) && html[j] !== '/' && html[j] !== '>') j++;
        out += html.slice(lt, j);
        // attributes, up to the tag's closing '>'
        while (j < n) {
            let k = j;
            while (k < n && (isTagSpace(html[k]) || html[k] === '/')) k++;
            out += html.slice(j, k);
            j = k;
            if (j >= n) break;
            if (html[j] === '>') {
                out += '>';
                j++;
                break;
            }
            k = j + 1; // a name may start with '='
            while (k < n && !isTagSpace(html[k]) && html[k] !== '/' && html[k] !== '>' && html[k] !== '=') k++;
            const isStyle = html.slice(j, k).toLowerCase() === 'style';
            out += html.slice(j, k);
            j = k;
            while (k < n && isTagSpace(html[k])) k++;
            if (html[k] !== '=') continue;
            k++;
            while (k < n && isTagSpace(html[k])) k++;
            out += html.slice(j, k);
            j = k;
            const quote = html[j];
            if (quote === '"' || quote === "'") {
                const close = html.indexOf(quote, j + 1);
                const end = close < 0 ? n : close;
                const value = html.slice(j + 1, end);
                out += quote + (isStyle ? mapCss(value) : value) + (close < 0 ? '' : quote);
                j = close < 0 ? n : close + 1;
            } else {
                let end = j;
                while (end < n && !isTagSpace(html[end]) && html[end] !== '>') end++;
                const value = html.slice(j, end);
                out += isStyle ? mapCss(value) : value;
                j = end;
            }
        }
        i = j;
    }
    return out;
};

// CSS strings and url() values inside a style attribute (quotes arrive as entities in double-quoted
// attributes). Their contents are not CSS lengths, so no rewrite may touch them.
const CSS_LITERAL = /url\(\s*(?:&quot;[\s\S]*?&quot;|&#0*39;[\s\S]*?&#0*39;|&apos;[\s\S]*?&apos;|"[^"]*"|'[^']*'|[^)]*)\s*\)|&quot;[\s\S]*?&quot;|&#0*39;[\s\S]*?&#0*39;|&apos;[\s\S]*?&apos;|"[^"]*"|'[^']*'/gi;

const replaceOutsideCssLiterals = (css: string, pattern: RegExp, replacer: (match: string, value: string) => string): string => {
    let out = '';
    let last = 0;
    for (const m of css.matchAll(CSS_LITERAL)) {
        out += css.slice(last, m.index).replace(pattern, replacer) + m[0];
        last = m.index + m[0].length;
    }
    return out + css.slice(last).replace(pattern, replacer);
};

const mapEpStyleLengths = (html: string, pattern: RegExp, replacer: (match: string, value: string) => string): string => mapEpStyleAttributes(html, css => replaceOutsideCssLiterals(css, pattern, replacer));

/**
 * Replace every inline `Xpt` value with `calc(X / var(--ep-h-pt) * 100cqh)`. This
 * makes font-size, line-height, margin, padding, gap, image height etc. scale
 * proportionally with the plate's container height (cqh resolves against the
 * nearest ancestor with `container-type: size`). Only style attributes are rewritten:
 * a `data:` image whose payload holds a digit and "pt" stays decodable, and a
 * shopper's "5pt" stays text.
 */
export const transformPtToCqh = (html: string): string => mapEpStyleLengths(html, /(-?(?:\d+(?:\.\d+)?|\.\d+))pt/gi, (_, sizePt) => `calc(${sizePt} / var(--ep-h-pt) * 100cqh)`);

/**
 * Stored `font-size: Npt` declarations to the cqh form, for the surfaces that convert font sizes only
 * (the CAF approval preview and fit check in ep-editor.tsx). Style attributes only, like transformPtToCqh.
 */
export const transformFontSizePtToCqh = (html: string): string => mapEpStyleLengths(html, /font-size:\s*([\d.]+)pt/gi, (_, sizePt) => `font-size: calc(${sizePt} / var(--ep-h-pt) * 100cqh)`);

/** Convert cqh font-sizes back to pt for storage/backend. Reverses the cqh transform. */
export const transformCqhToPt = (html: string): string => mapEpStyleLengths(html, /font-size:\s*calc\(\s*([\d.]+)\s*\/\s*(?:var\(--ep-h-pt\)|72)\s*\*\s*100cqh\)/gi, (_, sizePt) => `font-size: ${sizePt}pt`);

/**
 * Shared DOMPurify configuration for engraved-plate HTML content. Allows the
 * tags/attributes the rich-text editor and templates produce, plus `data:` URIs
 * so SVG/PNG image placeholders render.
 */
export const sanitizeEPContent = (html: string): string =>
    DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'span', 'ul', 'ol', 'li', 'div', 'img'],
        ALLOWED_ATTR: ['style', 'class', 'src', 'alt', 'width', 'height', 'data-ep-fit-width', 'data-ep-fit-max-pt', 'data-ep-fit-line-pt', 'data-ep-fit-weight', 'data-ep-fit-max-width-in'],
        ALLOW_DATA_ATTR: true,
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    });

const EP_FIT_WIDTH_ATTR = 'data-ep-fit-width';
// The fit spans of the rows one wrap line was split into (ep_lines[].wrap_chars) carry the line's number here: each row
// is fitted on its own, then all of them take the smallest fitted size, so the line's two rows print at one size.
export const EP_FIT_GROUP_ATTR = 'data-ep-fit-group';

// ---------------------------------------------------------------------------
// Optical (ink-box) centering — parity contract shared with the WebNexus PDF
// renderer (EngravedPlatePDF.cs, EngravedPlatePdfRenderer): on non-template
// plates (EPMethodID 3 Custom / 4 SingleDoc / 6 Admin) with 1-3 visible
// authored lines and no template artifacts, the plate centers the tight
// glyph-ink box — first visible line's highest ink to last visible line's
// lowest ink — instead of the line-box stack, and drops edge-blank lines.
// Interior spacing, wrapping and everything outside the gate never change.
// ---------------------------------------------------------------------------

export const EP_INK_CENTER_MAX_LINES = 3;

// Mirrors InkCenterOverridesAlign in EngravedPlatePDF.cs: gated plates force
// horizontal centering even over an authored left/right alignment.
export const EP_INK_CENTER_OVERRIDES_ALIGN = true;

const isEpBlockVisible = (el: HTMLElement | null): boolean => !!el && (!!(el.textContent || '').trim() || !!el.querySelector('img'));

/**
 * Authored (pre-word-wrap) visible line count, matching the PDF parser: block
 * children are lines, interior `<br>`s split a block into further lines, list
 * items count individually, and blank lines (no text, no image) don't count.
 */
export const countEpAuthoredLines = (root: HTMLElement): number => {
    let count = 0;
    for (const block of Array.from(root.children) as HTMLElement[]) {
        const units = block.tagName === 'UL' || block.tagName === 'OL' ? (Array.from(block.children) as HTMLElement[]) : [block];
        for (const unit of units) {
            const segments = unit.innerHTML.split(/<br[^>]*>/i);
            if (segments.length === 1) {
                if (isEpBlockVisible(unit)) count++;
                continue;
            }
            for (const segment of segments) {
                const probe = document.createElement('div');
                probe.innerHTML = segment;
                if (isEpBlockVisible(probe)) count++;
            }
        }
    }
    return count;
};

/** Template markup (fit-to-width spans, flex logo strips) is never ink-centered. */
export const hasEpTemplateArtifacts = (root: HTMLElement): boolean => !!root.querySelector(`[${EP_FIT_WIDTH_ATTR}]`) || !!root.querySelector('[style*="space-between"]');

let epMeasureCtx: CanvasRenderingContext2D | null | undefined;
const getEpMeasureCtx = (): CanvasRenderingContext2D | null => {
    if (epMeasureCtx === undefined) epMeasureCtx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    return epMeasureCtx;
};

// Chrome rounds TextMetrics box values to whole px, which at the ~6-15px
// reference-box font sizes the plate renders at swamps the sub-pixel gap math.
// Measure at a fixed large size and scale down — font metrics are linear.
const EP_MEASURE_PX = 100;

type EpTextMetrics = {fontAscent: number; fontDescent: number; inkAscent: number; inkDescent: number; advance: number; inkLeft: number; inkRight: number};

const measureEpText = (ctx: CanvasRenderingContext2D, cs: CSSStyleDeclaration, fontPx: number, text: string): EpTextMetrics | null => {
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${EP_MEASURE_PX}px ${cs.fontFamily}`;
    const m = ctx.measureText(text);
    if (typeof m.fontBoundingBoxAscent !== 'number') return null;
    const k = fontPx / EP_MEASURE_PX;
    return {
        fontAscent: m.fontBoundingBoxAscent * k,
        fontDescent: m.fontBoundingBoxDescent * k,
        inkAscent: m.actualBoundingBoxAscent * k,
        inkDescent: m.actualBoundingBoxDescent * k,
        advance: m.width * k,
        inkLeft: -m.actualBoundingBoxLeft * k,
        inkRight: m.actualBoundingBoxRight * k,
    };
};

type EpLineVMetrics = {lineH: number; fontAscent: number; fontDescent: number; inkAscent: number; inkDescent: number};

// Vertical metrics of one rendered line: line-box height plus font-metric and
// tight-ink ascent/descent, all in layout px. Canvas TextMetrics is the browser
// twin of the PDF's SKFont metrics/MeasureText bounds — same TTFs both sides.
// Assumes the line's tallest span dominates the box (uniform-font lines in
// practice), the same simplification the PDF's max-over-runs makes.
const measureEpLineV = (block: HTMLElement, ctx: CanvasRenderingContext2D): EpLineVMetrics | null => {
    let lineH = 0;
    let fontAscent = 0;
    let fontDescent = 0;
    let inkAscent = 0;
    let inkDescent = 0;
    let any = false;

    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
        const text = (node.textContent || '').trim();
        if (!text || !node.parentElement) continue;
        const cs = getComputedStyle(node.parentElement);
        const fontPx = parseFloat(cs.fontSize) || 0;
        if (fontPx <= 0) continue;
        const m = measureEpText(ctx, cs, fontPx, text);
        if (!m) return null;
        any = true;
        lineH = Math.max(lineH, cs.lineHeight === 'normal' ? fontPx * 1.2 : parseFloat(cs.lineHeight) || fontPx * 1.2);
        fontAscent = Math.max(fontAscent, m.fontAscent);
        fontDescent = Math.max(fontDescent, m.fontDescent);
        inkAscent = Math.max(inkAscent, m.inkAscent);
        inkDescent = Math.max(inkDescent, m.inkDescent);
    }

    // inline images sit on the baseline and fill their box (PDF DrawImageRun)
    for (const img of Array.from(block.querySelectorAll('img'))) {
        const h = (img as HTMLElement).offsetHeight;
        if (h > 0) {
            any = true;
            lineH = Math.max(lineH, h);
            fontAscent = Math.max(fontAscent, h);
            inkAscent = Math.max(inkAscent, h);
        }
    }

    return any ? {lineH, fontAscent, fontDescent, inkAscent, inkDescent} : null;
};

// Horizontal ink lean of a single-visual-line block: how far the tight ink box
// sits from the advance-width center that text-align: center produces. Positive
// = shift right to fix. Wrapped blocks return null (each visual line would need
// its own shift). Mirrors the PDF's EdgeBearing correction in DrawLine.
const measureEpLineLean = (block: HTMLElement, ctx: CanvasRenderingContext2D): number | null => {
    const range = document.createRange();
    range.selectNodeContents(block);
    const rects = Array.from(range.getClientRects()).filter(r => r.width > 0.5 && r.height > 0.5);
    if (rects.length === 0) return null;
    if (rects.some(r => Math.abs(r.top - rects[0].top) > r.height / 2)) return null;

    let first: EpTextMetrics | null = null;
    let last: EpTextMetrics | null = null;
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
        const text = (node.textContent || '').trim();
        if (!text || !node.parentElement) continue;
        const cs = getComputedStyle(node.parentElement);
        const fontPx = parseFloat(cs.fontSize) || 0;
        if (fontPx <= 0) continue;
        const m = measureEpText(ctx, cs, fontPx, text);
        if (!m) return null;
        first = first || m;
        last = m;
    }
    if (!first || !last) return null;

    const leftBearing = first.inkLeft;
    const rightBearing = last.advance - last.inkRight;
    return (rightBearing - leftBearing) / 2;
};

/**
 * Apply (or, with `enabled` false / gate not met, remove) optical ink centering
 * on a rendered plate block. `blockEl` is the element that directly contains the
 * plate's block-level lines, inside a flex column that centers it. Hides
 * edge-blank lines, translates the block so the ink box is vertically centered,
 * and nudges single-line leans (> 1px) onto the horizontal center. Idempotent —
 * safe to re-run on every render; all mutations are tagged and reverted first.
 */
export const applyEpInkCentering = (blockEl: HTMLElement, enabled: boolean): void => {
    blockEl.style.transform = '';
    const blocks = Array.from(blockEl.children) as HTMLElement[];
    for (const b of blocks) {
        if (b.dataset.epInkHidden !== undefined) {
            b.style.display = '';
            delete b.dataset.epInkHidden;
        }
        if (b.dataset.epInkShifted !== undefined) {
            b.style.transform = '';
            delete b.dataset.epInkShifted;
        }
        if (b.dataset.epInkAlign !== undefined) {
            b.style.textAlign = b.dataset.epInkAlign;
            delete b.dataset.epInkAlign;
        }
    }
    if (!enabled) return;

    const ctx = getEpMeasureCtx();
    if (!ctx) return;

    const lineCount = countEpAuthoredLines(blockEl);
    if (lineCount < 1 || lineCount > EP_INK_CENTER_MAX_LINES) return;
    if (hasEpTemplateArtifacts(blockEl)) return;

    let i = 0;
    let j = blocks.length - 1;
    while (i <= j && !isEpBlockVisible(blocks[i])) {
        blocks[i].dataset.epInkHidden = '1';
        blocks[i].style.display = 'none';
        i++;
    }
    while (j >= i && !isEpBlockVisible(blocks[j])) {
        blocks[j].dataset.epInkHidden = '1';
        blocks[j].style.display = 'none';
        j--;
    }
    if (i > j) return;

    if (EP_INK_CENTER_OVERRIDES_ALIGN) {
        for (let k = i; k <= j; k++) {
            const authored = blocks[k].style.textAlign;
            if (authored && authored !== 'center') {
                blocks[k].dataset.epInkAlign = authored;
                blocks[k].style.textAlign = 'center';
            }
        }
    }

    // Same placement formula as the PDF's DrawLines/DrawLine pair: the baseline
    // sits at (lineH + ascent - descent) / 2 from the line-box top, so the dead
    // space above the first line's ink and below the last line's ink is known
    // without reading pixel geometry.
    const firstV = measureEpLineV(blocks[i], ctx);
    const lastV = i === j ? firstV : measureEpLineV(blocks[j], ctx);
    if (firstV && lastV) {
        const topGap = (firstV.lineH + firstV.fontAscent - firstV.fontDescent) / 2 - firstV.inkAscent;
        const bottomGap = (lastV.lineH - lastV.fontAscent + lastV.fontDescent) / 2 - lastV.inkDescent;
        const dy = (bottomGap - topGap) / 2;
        if (Math.abs(dy) > 0.25) blockEl.style.transform = `translateY(${dy.toFixed(2)}px)`;
    }

    for (let k = i; k <= j; k++) {
        const dx = measureEpLineLean(blocks[k], ctx);
        if (dx !== null && Math.abs(dx) > 1) {
            blocks[k].dataset.epInkShifted = '1';
            blocks[k].style.transform = `translateX(${dx.toFixed(2)}px)`;
        }
    }
};

// A 0.5"-high plate can technically fit two wrapped lines of >12pt text, but the
// engraving comes out too cramped to be acceptable, so that combination is flagged
// like an overflow: the red warning shows and add-to-cart is blocked until the
// shopper reduces the text or the font size.
export const EP_SHORT_PLATE_MULTILINE_MAX_PT = 12;
export const EP_SHORT_PLATE_MULTILINE_MAX_HEIGHT_IN = 0.5;

// Largest font-size in the plate HTML, in points. Handles both the stored pt form
// (font-size: 15pt) and the editor's live cqh form (font-size: calc(15 / var(--ep-h-pt) * 100cqh)).
export const maxEpFontPt = (html: string): number => {
    let max = 0;
    if (!html) return max;
    for (const m of html.matchAll(/font-size:\s*([\d.]+)pt/gi)) max = Math.max(max, parseFloat(m[1]));
    for (const m of html.matchAll(/font-size:\s*calc\(\s*([\d.]+)\s*\//gi)) max = Math.max(max, parseFloat(m[1]));
    return max;
};

// Number of visual text lines in a rendered element (wrapped lines count, empty
// paragraphs don't). Line-box fragments are grouped by vertical midpoint so runs
// that sit on the same line at different heights (e.g. superscripts) don't count
// as extra lines. The element must be attached/visible so client rects resolve.
export const countVisualTextLines = (rootEl: HTMLElement): number => {
    const lines: {mid: number; height: number}[] = [];
    const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
        if (!(node.textContent || '').trim()) continue;
        const range = document.createRange();
        range.selectNodeContents(node);
        for (const r of Array.from(range.getClientRects())) {
            if (r.width <= 0.5 || r.height <= 0.5) continue;
            const mid = r.top + r.height / 2;
            const line = lines.find(l => Math.abs(l.mid - mid) < (l.height + r.height) / 4);
            if (!line) lines.push({mid, height: r.height});
        }
    }
    return lines.length;
};

// The 0.5"-plate multi-line rule itself, shared by the live editor warning and the
// headless CAF doc-size check: >12pt text laid out on 2+ lines on a plate no taller
// than 0.5" reads as an over-wrap even though nothing is clipped.
export const isEpShortPlateOverwrap = (contentHtml: string, rootEl: HTMLElement, plateHeightIn: number): boolean => plateHeightIn > 0 && plateHeightIn <= EP_SHORT_PLATE_MULTILINE_MAX_HEIGHT_IN && maxEpFontPt(contentHtml) > EP_SHORT_PLATE_MULTILINE_MAX_PT && countVisualTextLines(rootEl) >= 2;

// Letter-spacing in em of the measured font size. Admin-authored template markup may
// use px or pt instead of em; convert via the element's point size (1px = 0.75pt).
const parseLetterSpacingEm = (letterSpacing: string, fontPt: number): number => {
    const em = letterSpacing.match(/^([\d.]+)em$/i);
    if (em) return parseFloat(em[1]);
    if (fontPt > 0) {
        const px = letterSpacing.match(/^([\d.]+)px$/i);
        if (px) return (parseFloat(px[1]) * 0.75) / fontPt;
        const pt = letterSpacing.match(/^([\d.]+)pt$/i);
        if (pt) return parseFloat(pt[1]) / fontPt;
    }
    return 0;
};

// The point size a template element renders at, from its own or nearest ancestor
// inline style. Accepts pt and px (1px = 0.75pt); anything else (calc()/cqh/em or
// absent) is unresolvable and returns NaN.
const parseInheritedFontPt = (fontSize: string): number => {
    const m = fontSize.match(/^([\d.]+)(pt|px)$/i);
    if (!m) return NaN;
    const n = parseFloat(m[1]);
    return m[2].toLowerCase() === 'px' ? n * 0.75 : n;
};

const measureFittedTextWidth = (ctx: CanvasRenderingContext2D, text: string, fontPt: number, weight: string, family: string, letterSpacingEm = 0): number => {
    ctx.font = `${weight} ${fontPt}pt ${family}`;
    let width = ctx.measureText(text).width;
    if (letterSpacingEm > 0 && text.length > 1) {
        width += (text.length - 1) * letterSpacingEm * fontPt;
    }
    return width;
};

// Resolve an inline style property from the element or its nearest styled ancestor.
// The fit tree is detached (no computed styles), but template HTML carries all its
// styling inline, so walking parent inline styles reproduces CSS inheritance. Used by
// the auto-fit line spans, which are bare markers injected into the template's styled
// line divs at token-substitution time and inherit their font from them.
const inheritedInlineStyle = (el: HTMLElement, prop: 'fontSize' | 'fontFamily' | 'fontWeight' | 'letterSpacing' | 'textTransform'): string => {
    let node: HTMLElement | null = el;
    while (node) {
        const v = node.style ? node.style[prop] : '';
        if (v) return v;
        node = node.parentElement;
    }
    return '';
};

export const applyEpFitToWidth = (html: string, plateWidthIn: number): string => {
    if (!html || typeof document === 'undefined' || !html.includes(EP_FIT_WIDTH_ATTR)) return html;

    const wrap = document.createElement('div');
    wrap.innerHTML = html;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return html;

    const groups = new Map<string, {el: HTMLElement; pt: number; linePt: string | null}[]>();

    wrap.querySelectorAll<HTMLElement>(`[${EP_FIT_WIDTH_ATTR}]`).forEach(el => {
        const group = el.getAttribute(EP_FIT_GROUP_ATTR);
        const stripFitAttrs = () => {
            el.removeAttribute(EP_FIT_WIDTH_ATTR);
            el.removeAttribute('data-ep-fit-max-pt');
            el.removeAttribute('data-ep-fit-line-pt');
            el.removeAttribute('data-ep-fit-weight');
            el.removeAttribute('data-ep-fit-max-width-in');
            el.removeAttribute(EP_FIT_GROUP_ATTR);
        };

        // Explicit data attributes (hardcoded templates) win; auto-fit marker spans
        // fall back to the font the surrounding template markup gives the line. When
        // the line's size can't be resolved (calc()/cqh/em or absent) skip fitting
        // entirely rather than restamp the line at a wrong default size.
        const maxPtAttr = el.getAttribute('data-ep-fit-max-pt');
        const maxFontPt = maxPtAttr ? parseFloat(maxPtAttr) || 12 : parseInheritedFontPt(inheritedInlineStyle(el, 'fontSize'));
        if (!isFinite(maxFontPt) || maxFontPt <= 0) {
            stripFitAttrs();
            return;
        }

        // '900' is the legacy default for the attr path (hardcoded fit divs always emit
        // the attr, so it is effectively unreachable there); the CSS initial weight is
        // the right assumption for inherited-context marker spans.
        const weight = el.getAttribute('data-ep-fit-weight') || inheritedInlineStyle(el, 'fontWeight') || (maxPtAttr ? '900' : '400');
        const family = inheritedInlineStyle(el, 'fontFamily') || 'Roboto, sans-serif';
        const text = el.textContent || '';
        if (!text) {
            stripFitAttrs();
            return;
        }

        const displayText = inheritedInlineStyle(el, 'textTransform') === 'uppercase' ? text.toUpperCase() : text;
        const letterSpacingEm = parseLetterSpacingEm(inheritedInlineStyle(el, 'letterSpacing') || '', maxFontPt);

        const maxWidthInOverride = el.getAttribute('data-ep-fit-max-width-in');
        const effectiveWidthIn = maxWidthInOverride ? parseFloat(maxWidthInOverride) : plateWidthIn;
        // Hardcoded fit divs keep the empirically tuned 158px/2.5" measuring space they
        // were calibrated against. Marker spans (inherited path) use the physical CSS
        // ratio instead — the canvas resolves an X-pt font at 4/3 px and plate-space pt
        // is 72/inch, so text occupies (measured px)/96 plate inches; budgeting at
        // 63.2px/in would over-shrink to ~66% of the stated width and turn the 0.25"
        // side margins into ~0.68".
        const pxPerIn = maxPtAttr ? 158 / 2.5 : 96;
        const maxWidthPx = effectiveWidthIn * pxPerIn;

        // The specified size is a hard ceiling: the fit only ever shrinks from it when
        // the text would cross into the side margins, never enlarges. The 6pt legibility
        // floor yields to a smaller specified size so it can't bump text above it.
        let fontPt = maxFontPt;
        const floorPt = Math.min(6, maxFontPt);

        while (fontPt >= floorPt) {
            if (measureFittedTextWidth(ctx, displayText, fontPt, weight, family, letterSpacingEm) <= maxWidthPx) break;
            fontPt -= 0.25;
        }

        const fittedPt = Math.min(Math.max(fontPt, floorPt), maxFontPt);
        const linePt = el.getAttribute('data-ep-fit-line-pt');
        el.style.fontSize = `${fittedPt}pt`;
        el.style.lineHeight = linePt ? `${linePt}pt` : `${fittedPt}pt`;
        el.style.whiteSpace = 'nowrap';
        stripFitAttrs();
        if (group) {
            const members = groups.get(group) ?? [];
            members.push({el, pt: fittedPt, linePt});
            groups.set(group, members);
        }
    });

    groups.forEach(members => {
        const pt = Math.min(...members.map(m => m.pt));
        for (const m of members) {
            m.el.style.fontSize = `${pt}pt`;
            m.el.style.lineHeight = m.linePt ? `${m.linePt}pt` : `${pt}pt`;
        }
    });

    return wrap.innerHTML;
};

// ---------------------------------------------------------------------------
// Fit to height (a template's settingsJSON.fit_height_margin_pt, WEB/INST-PLATES.md section 12):
// after the width fit, the template's text as a whole shrinks just enough that its ink keeps the
// margin from the plate's top and bottom edges. Every pt length in the style attributes scales by
// one factor (sizes, line-heights, margins, divider images), so the design keeps its proportions
// and the stored HTML stays plain pt, which the frame canvas and the print PDF read as they are.
// ---------------------------------------------------------------------------

// The smallest factor the fit scales a template by: a template that needs more has a size problem
// the fit should not hide.
export const EP_FIT_HEIGHT_MIN_SCALE = 0.5;

/** Every `Npt` length in the style attributes times `factor`, to 0.01pt. Style attributes only, like transformPtToCqh. */
export const scaleEpPtLengths = (html: string, factor: number): string =>
    mapEpStyleLengths(html, /(-?(?:\d+(?:\.\d+)?|\.\d+))pt/gi, (_, sizePt) => `${Math.round(parseFloat(sizePt) * factor * 100) / 100}pt`);

// The template block laid out at the plate's width: its height and its ink's top and bottom, in pt
// from the block's top. Ink is each text run's glyph box (canvas metrics, as the ink centring above
// measures it) and each image's box.
const measureEpTemplateInk = (html: string, plateWidthIn: number, ctx: CanvasRenderingContext2D): {blockPt: number; inkTopPt: number; inkBottomPt: number} | null => {
    const host = document.createElement('div');
    host.style.cssText = `position: absolute; left: -10000px; top: 0; visibility: hidden; width: ${plateWidthIn * 96}px; font-size: 16px; line-height: ${EP_INHERITED_LINE_HEIGHT};`;
    // flow-root: no margin collapses through the block's edges, as in the flex item that holds the rows on the frame canvas
    host.innerHTML = `<div style="position: relative; width: 100%; display: flow-root;">${html}</div>`;
    document.body.appendChild(host);
    try {
        const block = host.firstElementChild as HTMLElement;
        const blockTop = block.getBoundingClientRect().top;
        let top = Infinity;
        let bottom = -Infinity;
        const range = document.createRange();
        const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
        let node: Node | null;
        while ((node = walker.nextNode())) {
            const text = (node.textContent || '').trim();
            if (!text || !node.parentElement) continue;
            const cs = getComputedStyle(node.parentElement);
            const fontPx = parseFloat(cs.fontSize) || 0;
            if (fontPx <= 0) continue;
            const m = measureEpText(ctx, cs, fontPx, cs.textTransform === 'uppercase' ? text.toUpperCase() : text);
            if (!m) return null;
            range.selectNodeContents(node);
            for (const r of Array.from(range.getClientRects())) {
                if (r.width <= 0 || r.height <= 0) continue;
                // the run's box is its font's ascent and descent around the baseline; the ink sits inside it
                top = Math.min(top, r.top + m.fontAscent - m.inkAscent);
                bottom = Math.max(bottom, r.bottom - m.fontDescent + m.inkDescent);
            }
        }
        for (const img of Array.from(block.querySelectorAll('img'))) {
            const r = img.getBoundingClientRect();
            if (r.height <= 0) continue;
            top = Math.min(top, r.top);
            bottom = Math.max(bottom, r.bottom);
        }
        if (!isFinite(top) || !isFinite(bottom)) return null;
        return {blockPt: block.getBoundingClientRect().height * 0.75, inkTopPt: (top - blockTop) * 0.75, inkBottomPt: (bottom - blockTop) * 0.75};
    } finally {
        host.remove();
    }
};

/**
 * Shrink a template's resolved, width-fitted HTML so its ink keeps `marginPt` from the plate's top
 * and bottom edges (the frame canvas and the PDF centre the block on the plate). HTML that already
 * keeps it comes back unchanged; nothing ever grows. Needs a laid-out DOM with the template's web
 * fonts loaded (without them the fallback face is measured; updateTemplateContent fits again when
 * they arrive); without a DOM the HTML comes back as it is.
 */
export const applyEpFitToHeight = (html: string, plateWidthIn: number, plateHeightIn: number, marginPt: number): string => {
    if (!html || !(marginPt > 0) || !(plateWidthIn > 0) || !(plateHeightIn > 0) || typeof document === 'undefined' || !document.body) return html;
    const ctx = getEpMeasureCtx();
    if (!ctx) return html;
    // from the plate's middle to the margin line, above and below alike
    const room = (plateHeightIn * 72) / 2 - marginPt;
    if (!(room > 0)) return html;

    let out = html;
    let scale = 1;
    // Uniform scaling moves the ink linearly, so one pass lands on the margin; the next ones absorb layout rounding.
    for (let pass = 0; pass < 3; pass++) {
        const ink = measureEpTemplateInk(out, plateWidthIn, ctx);
        if (!ink) return out;
        const above = ink.blockPt / 2 - ink.inkTopPt;
        const below = ink.inkBottomPt - ink.blockPt / 2;
        const step = Math.min(1, above > 0 ? room / above : 1, below > 0 ? room / below : 1);
        if (step >= 0.999) return out;
        const next = Math.max(EP_FIT_HEIGHT_MIN_SCALE, scale * step);
        if (next >= scale) return out;
        scale = next;
        out = scaleEpPtLengths(html, scale);
    }
    return out;
};
